import { create } from 'zustand';
import type { PurchaseOrderCsvRow } from '../../hooks/usePurchaseOrderCsvParser';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type WizardStep = 'upload' | 'validation' | 'preview' | 'processing' | 'result';

export interface ValidationError {
  row: number;
  sku: string;
  reason: string;
  quantity?: number;
}

export interface PreviewRow {
  sku: string;
  productId: string;
  currentStock: number;
  quantity: number;
  unitCost: number | null;
  totalCostImpact: number | null;
  isNewAtLocation: boolean;
  rowCount: number;
}

export interface ProcessingResult {
  successCount: number;
  failedCount: number;
  validationErrors: ValidationError[];
  executionErrors: string[];
  mode?: 'complete' | 'partial' | 'blocked' | 'network_error';
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

interface PurchaseOrderCsvUploadState {
  step: WizardStep;
  file: File | null;
  parsedRows: PurchaseOrderCsvRow[];
  validationErrors: ValidationError[];
  blockOnErrors: boolean;
  previewRows: PreviewRow[];
  skippedRows: PurchaseOrderCsvRow[];
  processing: { total: number; processed: number; failed: number };
  result: ProcessingResult | null;
}

interface PurchaseOrderCsvUploadActions {
  setStep: (step: WizardStep) => void;
  setFile: (file: File | null) => void;
  setParsedRows: (rows: PurchaseOrderCsvRow[]) => void;
  setValidationErrors: (errors: ValidationError[]) => void;
  setBlockOnErrors: (block: boolean) => void;
  setPreviewRows: (rows: PreviewRow[]) => void;
  setSkippedRows: (rows: PurchaseOrderCsvRow[]) => void;
  startProcessing: (total: number) => void;
  tickProcessed: () => void;
  tickFailed: () => void;
  setResult: (result: ProcessingResult) => void;
  setMode: (mode: ProcessingResult['mode']) => void;
  clearResult: () => void;
  reset: () => void;
}

type PurchaseOrderCsvUploadStore = PurchaseOrderCsvUploadState & PurchaseOrderCsvUploadActions;

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const initialState: PurchaseOrderCsvUploadState = {
  step: 'upload',
  file: null,
  parsedRows: [],
  validationErrors: [],
  blockOnErrors: true,
  previewRows: [],
  skippedRows: [],
  processing: { total: 0, processed: 0, failed: 0 },
  result: null,
};

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const usePurchaseOrderCsvUpload = create<PurchaseOrderCsvUploadStore>((set) => ({
  ...initialState,

  setStep: (step) => set({ step }),

  setFile: (file) => set({ file }),

  setParsedRows: (rows) => set({ parsedRows: rows }),

  setValidationErrors: (errors) => set({ validationErrors: errors }),

  setBlockOnErrors: (block) => set({ blockOnErrors: block }),

  setPreviewRows: (rows) => set({ previewRows: rows }),

  setSkippedRows: (rows) => set({ skippedRows: rows }),

  startProcessing: (total) =>
    set({
      step: 'processing',
      processing: { total, processed: 0, failed: 0 },
    }),

  tickProcessed: () =>
    set((s) => ({
      processing: {
        ...s.processing,
        processed: s.processing.processed + 1,
      },
    })),

  tickFailed: () =>
    set((s) => ({
      processing: {
        ...s.processing,
        processed: s.processing.processed + 1,
        failed: s.processing.failed + 1,
      },
    })),

  setResult: (result) => set({ step: 'result', result }),

  setMode: (mode) =>
    set((s) => ({
      result: s.result ? { ...s.result, mode } : s.result,
    })),

  clearResult: () => set({ result: null }),

  reset: () => set(initialState),
}));
